import React from 'react';
import { Link } from 'react-router-dom';
import { DEVICE_RENTAL_BATCH_PAYMENT_BILLING_ROUTER_URL } from '@Router/routerPath';
import { Box, Chip } from '@curves/ui-base';

import { parseDate } from '@Util/moment.util';
import { formatCurrencyFn, isEmpty } from '@Util/utils';
import { matchTaskStatusColors } from '@Local/DeviceRental/DeviceRental.util';
import DateTimeDisplay from '@Local/Components/RowLabel/DateTimeDisplay';
import { OverflowMenu } from '@Local/Components';
import {DATE_TIME_STANDARD} from '@Constants/appConfig';
/**
 * 判斷任務狀態是否為可查看的狀態
 * @param {string|number} taskStatus - 任務狀態
 * @returns {boolean} 是否可查看
 */
const isViewableStatus = (taskStatus) => ['3', '4', '5'].includes(String(taskStatus));
/**
 * 操作選單對應表
 * @type {Object}
 */
const actionMap = {
    view: {
        text: '查看',
        condition: ({ taskStatus }) => isViewableStatus(taskStatus),
        component: Link,
        to: ({ taskID }) => DEVICE_RENTAL_BATCH_PAYMENT_BILLING_ROUTER_URL(taskID),
    },
    settle: {
        text: '進行扣款',
        condition: ({ canSettle }) => canSettle,
        onClick: ({ taskID }, { dialogRef }) => dialogRef?.current?.openConfirmSettleBatchPaymentTaskDialog?.({ taskID }),
    },
    delete: {
        text: '刪除',
        condition: ({ canDelete }) => canDelete,
        onClick: ({ taskID }, { dialogRef }) => dialogRef?.current?.openConfirmDeleteBatchPaymentTaskDialog?.({ taskID }),
    },
};
/**
 * 根據傳入條件與操作定義，產生對應的選單資料。
 *
 * @param {Object} data - 該列資料（如任務狀態、權限等）
 * @param {Object} actionMap - 定義各操作條件與行為的設定物件
 * @param {Object} resources - 額外傳入的上下文（如 fn、ref、router 等）
 * @returns {Array<Object>} menuData - OverflowMenu 可用的選單資料陣列
 */
const getMenuData = (data, actionMap, resources = {}) => {
    return Object.values(actionMap)
        .filter((action) => action.condition?.(data))
        .map((action) => ({
            text: action.text,
            component: action.component,
            to: action.to?.(data),
            onClick: action.onClick ? () => action.onClick(data, resources) : undefined,
        }));
};
/**
 * 取得設備租賃批次付款表格欄位配置
 *
 * 功能：
 * - 定義表格各欄位的顯示格式
 * - 包含操作選單和狀態顯示
 * - 支援連結跳轉和格式化顯示
 *
 * @returns {Array<Object>} 表格欄位配置陣列
 */
export const getDeviceRentalBatchPaymentRowLabel = () => {
    return [
        {
            cellKey: 'fileTaskID',
            headerLabel: '編號',
            formatCell: (value, { taskID, taskStatus }) => {
                const canView = isViewableStatus(taskStatus);
                if (!canView) return value;
                return (
                    <Link className={'text-decoration-underline text-secondary'} to={DEVICE_RENTAL_BATCH_PAYMENT_BILLING_ROUTER_URL(taskID)}>
                        {value}
                    </Link>
                );
            },
        },
        {
            cellKey: 'settleDate',
            headerLabel: '送件扣款日期',
            formatCell: (value) => {
                if (!value) return '-';
                return <DateTimeDisplay date={value} />;
            },
        },
        {
            cellKey: 'taskStatusText',
            headerLabel: '狀態',
            align: 'center',
            formatCell: (taskStatusText, { taskStatus }) => {
                return <Chip color={matchTaskStatusColors(taskStatus)} label={taskStatusText} />;
            },
        },
        {
            cellKey: 'settleDate',
            headerLabel: '扣款月份',
            align: 'center',
            formatCell: (value) => {
                return <Box>{parseDate(value, DATE_TIME_STANDARD.DISPLAYDATEOFMONTH)}</Box>;
            },
        },
        {
            cellKey: 'totalAmount',
            headerLabel: '總筆數',
            align: 'center',
        },
        {
            cellKey: 'totalPrice',
            headerLabel: '總金額',
            align: 'right',
        },
        {
            cellKey: 'settleSuccessAmount',
            headerLabel: '扣款成功筆數',
            align: 'center',
            formatCell: (value) => {
                if (!value) return '-';
                return <Box className={'text-success'}>{value}</Box>;
            },
        },
        {
            cellKey: 'settleFailureAmount',
            headerLabel: '扣款失敗筆數',
            align: 'center',
            formatCell: (value) => {
                if (!value) return '-';
                return <Box className={'text-error'}>{value}</Box>;
            },
        },
        {
            cellKey: 'settleSuccessPrice',
            headerLabel: '實際扣款金額',
            align: 'right',
            formatCell: (value) => {
                if (!value) return '-';
                return formatCurrencyFn(value, '$');
            },
        },
        {
            cellKey: 'settleUserDisplayName',
            headerLabel: '扣款人員',
            align: 'center',
            formatCell: (value) => {
                if (!value) return '-';
                return value;
            },
        },
        {
            cellKey: 'more',
            headerLabel: '',
            width: 6.8,
            formatCell: (_, rowData, { dialogRef }) => {
                const menuData = getMenuData(rowData, actionMap, { dialogRef });
                return <OverflowMenu menuData={menuData} enabled={!isEmpty(menuData)} />;
            },
        },
    ];
};
